import React from 'react';
import TextButton from '../Button/TextButton';
import FixPlaceholderButton from '../FixPlaceholderButton/FixPlaceholderButton';
// @ts-ignore
import copyIcon from '../../image-assets/icon.copy.svg';
import editIcon from '../../image-assets/icon.edit.svg';
import translateIcon from '../../image-assets/icon.tone.svg';



interface EditableCellProps {
  rowKey: string;
  columnKey: string;
  columnIndex: number;
  content: string;
  isCellEditable: boolean;
  isTargetLang: boolean;
  hasSourceText: boolean;
  isMissingPlaceholders: boolean;
  isTranslating: boolean;
  isRowSelected: boolean;
  hasChanges: boolean;
  isEditing: boolean;
  editingValue: string;
  permissions: any;
  onRowSelect?: (rowKey: string, selected: boolean) => void;
  onDelete?: (rowKey: string) => void;
  onCellFocus: (rowKey: string, columnKey: string) => void;
  onCellBlur: (e: React.FocusEvent<HTMLDivElement>) => void;
  onCellInput: (e: React.FormEvent<HTMLDivElement>) => void;
  onCellKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onTranslateCell: (rowKey: string, columnKey: string, sourceText: string) => Promise<{ result: string } | null>;
  onSaveClick: (e: React.MouseEvent<HTMLButtonElement>, rowKey: string, columnKey: string, columnIndex: number) => void;
  getSourceTextForRow: (rowKey: string) => string;
  cellRef: (el: HTMLDivElement | null) => void;
  contentRef: (el: HTMLDivElement | null) => void;
}

const EditableCell: React.FC<EditableCellProps> = React.memo(({
  rowKey,
  columnKey,
  columnIndex,
  content,
  isCellEditable,
  isTargetLang,
  hasSourceText,
  isMissingPlaceholders,
  isTranslating,
  isRowSelected,
  hasChanges,
  isEditing,
  editingValue,
  permissions,
  onRowSelect,
  onDelete,
  onCellFocus,
  onCellBlur,
  onCellInput,
  onCellKeyDown,
  onTranslateCell,
  onSaveClick,
  getSourceTextForRow,
  cellRef,
  contentRef
}) => {
  const cellKey = `${rowKey}-${columnKey}`;
  


  return (
    <div
      key={cellKey}
      id={cellKey}
      data-cell-key={cellKey}
      data-row={rowKey}
      data-col={columnKey}
      data-column={columnIndex}
      className={`editable-table-cell ${columnIndex === 0 ? 'key-cell' : 'translation-cell'} ${isTranslating ? 'translating' : ''}`}
      ref={cellRef}
      onClick={(e) => {
        if (columnIndex === 0) {
          const target = e.target as HTMLElement;
          if (target.classList.contains('row-checkbox')) {
            const isChecked = (target as HTMLInputElement).checked;
            onRowSelect?.(rowKey, isChecked);
          }
        }
      }}
    >
      {columnIndex === 0 && (
        <input
          type="checkbox"
          className="row-checkbox"
          checked={isRowSelected}
          onChange={(e) => onRowSelect?.(rowKey, e.target.checked)}
        />
      )}
      
      {/* Container for the editable content */}
      <div className="cell-inner-container">
        {/* Actual editable content */}
        <div 
          className={`cell-content ${isMissingPlaceholders ? 'missing-placeholder' : ''}`}
          data-missing-placeholders={isMissingPlaceholders}
          contentEditable={columnIndex !== 0 && isCellEditable}
          suppressContentEditableWarning
          tabIndex={columnIndex !== 0 && isCellEditable ? 0 : -1}
          title={columnIndex === 0 ? content : undefined}
          onFocus={(e) => {
            if (columnIndex !== 0 && isCellEditable) {
              onCellFocus(rowKey, columnKey);
            }
          }}
          onBlur={onCellBlur}
          onInput={onCellInput}
          onKeyDown={onCellKeyDown}
          ref={contentRef}
        >
          {/* Content will be set by ref callback */}
        </div>
      </div>
      
      {columnIndex === 0 && (
        <div className="icon-container">
          <img 
            src={copyIcon} 
            alt="Copy" 
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(content).then(() => {
                const img = e.target as HTMLImageElement;
                const originalOpacity = img.style.opacity;
                img.style.opacity = '1';
                setTimeout(() => {
                  img.style.opacity = originalOpacity;
                }, 200);
              });
            }}
          />
          {onDelete && permissions.canManageKeys && (
            <img 
              src={editIcon} 
              alt="Edit" 
              onClick={(e) => {
                e.stopPropagation();
                onDelete(rowKey);
              }}
            />
          )}
        </div>
      )}
      
      {/* Show translate button container with save and fix placeholder buttons */}
      {(isTargetLang && hasSourceText) || isMissingPlaceholders || hasChanges ? (
        <div className="cell-translate-button-container">
          {isTargetLang && hasSourceText && (
            <TextButton
              variant="text"
              size="small"
              className="cell-translate-button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                
                requestAnimationFrame(() => {
                  onTranslateCell(rowKey, columnKey, getSourceTextForRow(rowKey));
                });
              }}
              onMouseDown={e => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <img src={translateIcon} alt="Translate" />
              Translate
            </TextButton>
          )}
          {isMissingPlaceholders && (
            <FixPlaceholderButton
              onClick={() => onTranslateCell(rowKey, columnKey, getSourceTextForRow(rowKey))}
            />
          )}
          {hasChanges && (
            <TextButton
              variant="primary"
              size="small"
              className="cell-save-button"
              onClick={(e) => onSaveClick(e, rowKey, columnKey, columnIndex)}
            >
              Save
            </TextButton>
          )}
        </div>
      ) : null}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for optimal performance
  // Only re-render if this specific cell's props changed
  return (
    prevProps.rowKey === nextProps.rowKey &&
    prevProps.columnKey === nextProps.columnKey &&
    prevProps.content === nextProps.content &&
    prevProps.isCellEditable === nextProps.isCellEditable &&
    prevProps.isTargetLang === nextProps.isTargetLang &&
    prevProps.hasSourceText === nextProps.hasSourceText &&
    prevProps.isMissingPlaceholders === nextProps.isMissingPlaceholders &&
    prevProps.isTranslating === nextProps.isTranslating &&
    prevProps.isRowSelected === nextProps.isRowSelected &&
    prevProps.hasChanges === nextProps.hasChanges &&
    prevProps.isEditing === nextProps.isEditing &&
    prevProps.editingValue === nextProps.editingValue
  );
});

EditableCell.displayName = 'EditableCell';

export default EditableCell;
